import type { PrescriptionImage } from '@/entities/performance/api/performance.api';
import legendDot from '@/assets/figma/perf-legend-dot.svg';
import legendDotSm from '@/assets/figma/perf-legend-dot-sm.svg';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { cn } from '@/shared/lib/utils';
import { Check, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * 실적 화면 공통 조각 — 화면 두 개(M-08 · M-09)에서만 쓰므로 pages 안에 둔다.
 */

/** Figma 배지 (10.5px · px8 py3 · radius 5) — 기존 Badge보다 한 단계 작은 실적 배지 */
export const PerfBadge = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center rounded-badge px-2 py-[3px] text-[10.5px] leading-4 font-medium whitespace-nowrap',
      className,
    )}
  >
    {children}
  </span>
);

/** 배지 색 — 실적 화면 전용 (Figma 값) */
export const PERF_BADGE = {
  settled: 'bg-success-soft text-success',
  pending: 'bg-warning-soft text-warning',
  info: 'bg-[#f1f6ff] text-[#0b4f91]', // 처방 · 정산 전
  muted: 'bg-muted text-ink-6',
  faint: 'bg-muted text-[#c6c6c6]',
} as const;

/**
 * 엑셀 내보내기 — onClick이 없으면 비활성으로 자리만 지킨다 (M-14 원칙)
 * M-09는 병의원 단위 export API를 쓰고, M-08(연간)은 API가 없어 비활성이다 (docs/api B2)
 */
export const ExcelExportButton = ({
  onClick,
  pending = false,
  title,
}: {
  onClick?: () => void;
  pending?: boolean;
  title?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick || pending}
    aria-busy={pending}
    title={title}
    className="flex h-[42px] w-[130px] shrink-0 items-center justify-center gap-1.5 rounded-md bg-muted text-sm leading-[21px] font-medium text-ink-2 hover:bg-[#ebebeb] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-muted"
  >
    {pending && <Loader2 className="size-4 animate-spin" />}
    엑셀 내보내기
  </button>
);

export interface ChipOption<T extends string | number> {
  value: T;
  label: string;
}

/**
 * 툴바 드롭다운 칩 "확정 금액 높은순 ▾" · "제약사 ▾" · "상태 ▾"
 * value가 undefined면 placeholder(칩 이름)를 보여준다.
 */
export const ChipMenu = <T extends string | number>({
  placeholder,
  value,
  options,
  onChange,
  allLabel,
}: {
  placeholder: string;
  value: T | undefined;
  options: ChipOption<T>[];
  onChange: (value: T | undefined) => void;
  /** 있으면 맨 위에 "전체"(선택 해제) 항목을 둔다 */
  allLabel?: string;
}) => {
  const selected = options.find((o) => o.value === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex h-[37px] shrink-0 items-center rounded-[9px] border bg-surface px-[13px] text-[12.5px] leading-[19px] whitespace-nowrap outline-none',
          selected && allLabel
            ? 'border-ink text-ink'
            : 'border-[#dbdbdb] text-ink-3',
        )}
      >
        {selected?.label ?? placeholder} ▾
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
        {allLabel && (
          <DropdownMenuItem onSelect={() => onChange(undefined)}>
            {allLabel}
            {value === undefined && <Check className="ml-auto size-4" />}
          </DropdownMenuItem>
        )}
        {options.map((o) => (
          <DropdownMenuItem key={o.value} onSelect={() => onChange(o.value)}>
            {o.label}
            {o.value === value && <Check className="ml-auto size-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** 증감 배지 "+12%" — 감소면 빨강 */
export const DeltaBadge = ({ value }: { value: number | null | undefined }) => {
  if (value === null || value === undefined) return null;
  const down = value < 0;
  return (
    <span
      className={cn(
        'rounded-badge px-[7px] py-0.5 text-[10.5px] leading-4 font-medium whitespace-nowrap',
        down ? 'bg-danger-soft text-danger' : 'bg-success-soft text-success',
      )}
    >
      {value > 0 ? '+' : ''}
      {Math.round(value * 10) / 10}%
    </span>
  );
};

/** 처방 (예상) · 정산 (확정) 범례 */
export const TrendLegend = ({ size = 'md' }: { size?: 'md' | 'sm' }) => {
  const sm = size === 'sm';
  const text = cn(
    'text-ink-3',
    sm ? 'text-[11.5px] leading-[17px]' : 'text-[12.5px] leading-[19px]',
  );
  return (
    <div className={cn('flex items-center', sm ? 'gap-3.5' : 'gap-4')}>
      <span className={cn('flex items-center', sm ? 'gap-1.5' : 'gap-[7px]')}>
        {sm ? (
          <img src={legendDotSm} alt="" width={8} height={8} />
        ) : (
          <img src={legendDot} alt="" width={9} height={9} />
        )}
        <span className={text}>처방 (예상)</span>
      </span>
      <span className={cn('flex items-center', sm ? 'gap-1.5' : 'gap-[7px]')}>
        <span
          className={cn(
            'rounded-[2px]',
            sm ? 'h-[11px] w-[9px] bg-[#c3eada]' : 'h-3 w-2.5 bg-[#34b87f]',
          )}
        />
        <span className={text}>정산 (확정)</span>
      </span>
    </div>
  );
};

/**
 * 이미지 검수 상태 (reviewStatusCode) — VERIFIED만 확인 완료. 문구는 서버 reviewStatus를 쓴다.
 * 정산 확정 여부(isConfirmed)와는 다른 축이다.
 */
export const ImageStatusBadge = ({ image }: { image: PrescriptionImage }) => {
  const tone =
    image.reviewStatusCode === 'VERIFIED'
      ? PERF_BADGE.settled
      : image.reviewStatusCode === 'REJECTED'
        ? 'bg-danger-soft text-danger'
        : PERF_BADGE.pending;
  return <PerfBadge className={tone}>{image.reviewStatus}</PerfBadge>;
};
